import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AlertTriangle, ArrowLeft, FilePenLine, Loader2, Scale } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { apiGet, apiPost, fmtDate } from "@/utils/api";

const resolutionLabel = {
  open: "Open",
  superseded_reentry: "Reopened for re-entry",
  test_rolled_back: "Rolled back (test data)",
  variance_accepted_pending_sap: "Variance accepted · waiting on SAP posting",
  variance_accepted_credit_chain_pending: "Invoice posted · credit note(s) in progress",
  variance_accepted_credit_chain_failed: "Invoice posted · credit note failed (needs attention)",
  variance_accepted_resolved: "Resolved · invoice + credit note(s) posted",
};

const resolutionTone = {
  variance_accepted_credit_chain_failed: "border-red-300 bg-red-50 text-red-900",
  variance_accepted_resolved: "border-emerald-200 bg-emerald-50 text-emerald-900",
};

const resolvedBannerMessage = {
  variance_accepted_credit_chain_failed: "This exception needs attention — the invoice posted but a correcting credit note failed. Check the error below and retry/escalate.",
};

/** Classify a variance line so the UI can show, in plain language, which of
 * the two proven SAP mechanisms will run for it once the invoice posts:
 *   - "overage"       → billed qty > received qty  → Credit Memo (item 003)
 *   - "price"         → billed rate > PO rate       → Subsequent Credit (item 006)
 *   - "overage+price"  → both                        → one of each, chained together
 * Thresholds mirror the backend's own decomposition (cents-level rounding). */
function classifyVariance(line) {
  const expectedQty = Number(line.expected_quantity ?? 0);
  const billedQty = Number(line.billed_quantity ?? 0);
  const expectedRate = Number(line.expected_unit_price ?? 0);
  const billedRate = Number(line.billed_unit_price ?? 0);
  const qtyOver = Math.round((billedQty - expectedQty) * 1000) / 1000 > 0.0005;
  const priceOver = Math.round((billedRate - expectedRate) * 100) / 100 > 0.005;
  if (qtyOver && priceOver) return "overage+price";
  if (qtyOver) return "overage";
  if (priceOver) return "price";
  return "other";
}

const varianceBadge = {
  overage: { label: "Quantity overage", className: "bg-orange-100 text-orange-800 border-orange-300" },
  price: { label: "Price variance", className: "bg-sky-100 text-sky-800 border-sky-300" },
  "overage+price": { label: "Quantity + price variance", className: "bg-purple-100 text-purple-800 border-purple-300" },
  other: { label: "Other exception", className: "bg-slate-100 text-slate-700 border-slate-300" },
};

export default function ThreeWayException() {
  const { exceptionId } = useParams();
  const navigate = useNavigate();
  const [record, setRecord] = useState(null);
  const [error, setError] = useState("");
  const [busyAction, setBusyAction] = useState("");

  useEffect(() => {
    apiGet(`/three-way-exceptions/${encodeURIComponent(exceptionId)}`)
      .then(setRecord)
      .catch((requestError) => setError(requestError.message || "Could not load this exception"));
  }, [exceptionId]);

  if (error) {
    const looksMissing = /not found|404/i.test(error);
    return (
      <main className="min-h-[calc(100vh-8rem)] bg-slate-50 p-4 sm:p-6" data-testid="three-way-exception-error-page">
        <div className="mx-auto max-w-3xl space-y-5">
          <Button variant="outline" onClick={() => navigate("/invoices/new")} data-testid="three-way-exception-error-back-button" className="gap-2">
            <ArrowLeft size={15} />Back to receipts
          </Button>
          <section className="border-l-4 border-red-600 bg-white p-5" data-testid="three-way-exception-error">
            <div className="flex gap-3">
              <AlertTriangle className="text-red-700" />
              <div className="flex-1">
                <h1 className="text-xl font-semibold text-slate-950">{looksMissing ? "Exception not found" : "Could not load this exception"}</h1>
                <p className="mt-2 text-sm text-slate-700" data-testid="three-way-exception-error-message">{error}</p>
                <p className="mt-3 text-xs text-slate-500">
                  Looked up exception ID: <span className="font-mono" data-testid="three-way-exception-error-id">{exceptionId}</span>
                </p>
                {looksMissing && (
                  <p className="mt-3 text-xs text-slate-600">Double-check the ID in the URL — the Due Invoicing worklist's <em>View exception</em> button has the correct link.</p>
                )}
              </div>
            </div>
          </section>
        </div>
      </main>
    );
  }
  if (!record) return <main className="p-6" data-testid="three-way-exception-loading">Loading exception…</main>;

  const isOpen = record.status === "open";

  const handleReenter = async () => {
    setBusyAction("reenter");
    try {
      const response = await apiPost(`/three-way-exceptions/${encodeURIComponent(exceptionId)}/reenter`, {});
      toast.success("Exception reopened — re-enter the supplier invoice now");
      const hubParam = response.materials_hub === "preview" ? "?hub=preview" : "";
      navigate(`/invoices/prepare/${encodeURIComponent(response.worklist_id)}${hubParam}`);
    } catch (requestError) {
      toast.error(requestError.message || "Could not reopen this exception");
    } finally {
      setBusyAction("");
    }
  };

  const handleAcceptVariance = async () => {
    setBusyAction("accept-variance");
    try {
      const response = await apiPost(`/three-way-exceptions/${encodeURIComponent(exceptionId)}/accept-with-credit-memo`, {});
      toast.success(`Variance accepted · ${response.variance_total} INR will be rebased via credit note(s) after SAP post`);
      // Skip the three-way-match re-prompt — go directly to the full invoice workspace
      // with the synthetic approved match attached. NewInvoice reads ``match`` + the
      // ``/three-way-matches/approved/<id>`` payload for billed values.
      const params = new URLSearchParams({
        source: response.source_type,
        reference: response.source_reference,
        worklist: response.worklist_id,
        match: response.match_id,
        prepare: "1",
      });
      if (response.materials_hub === "preview") params.set("hub", "preview");
      navigate(`/invoices/new?${params}`);
    } catch (requestError) {
      toast.error(requestError.message || "Could not accept the variance");
    } finally {
      setBusyAction("");
    }
  };

  return (
    <main className="min-h-[calc(100vh-8rem)] bg-slate-50 p-4 sm:p-6" data-testid="three-way-exception-page">
      <div className="mx-auto max-w-5xl space-y-5">
        <Button variant="outline" onClick={() => navigate("/invoices/new")} data-testid="three-way-exception-back-button" className="gap-2">
          <ArrowLeft size={15} />Back to receipts
        </Button>

        <section className="border-l-4 border-red-600 bg-white p-5" data-testid="three-way-exception-summary">
          <div className="flex gap-3">
            <AlertTriangle className="text-red-700" />
            <div className="flex-1">
              <p className="font-mono text-xs text-red-700" data-testid="three-way-exception-id">{record.exception_id}</p>
              <h1 className="mt-1 text-2xl font-semibold text-slate-950" data-testid="three-way-exception-heading">Three-way match exception</h1>
              <p className="mt-1 text-xs font-semibold uppercase tracking-wide text-slate-500" data-testid="three-way-exception-status">
                Status: {resolutionLabel[record.status] || record.status}
              </p>
              <p className="mt-2 text-sm text-slate-600">
                Normal SAP draft creation {isOpen ? "is blocked" : "was blocked"} for supplier invoice <strong className="font-mono">{record.supplier_invoice_number || "—"}</strong> on {fmtDate(record.supplier_invoice_date)} (GRN {record.grn_id || "—"}{record.site ? <> · Site <span className="font-mono" data-testid="three-way-exception-site">{record.site}</span>{record.buyer_entity_name ? <span className="text-slate-400"> ({record.buyer_entity_name})</span> : null}</> : null}). {isOpen ? "Pick a resolution path below." : "See how it was resolved below."}
              </p>
            </div>
          </div>

          <div className="mt-5 space-y-3" data-testid="three-way-exception-lines">
            {record.lines.map((line, index) => {
              const varianceType = classifyVariance(line);
              const badge = varianceBadge[varianceType];
              const reasonText = (line.reasons || [line.reason]).filter(Boolean).join(", ");
              return (
                <article key={index} className="border border-red-100 bg-red-50 p-3 text-sm" data-testid={`three-way-exception-line-${index}`}>
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="font-semibold">{line.material_code || "Invoice line"}{reasonText ? ` · ${reasonText}` : ""}</p>
                    <span
                      className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${badge.className}`}
                      data-testid={`three-way-exception-line-badge-${index}`}
                    >
                      {badge.label}
                    </span>
                  </div>
                  <p className="mt-1 text-xs">
                    Expected qty/rate: <span className="font-mono">{line.expected_quantity ?? "—"} / {line.expected_unit_price ?? "—"}</span>
                    <span className="mx-2 text-slate-400">·</span>
                    Billed qty/rate: <span className="font-mono">{line.billed_quantity ?? "—"} / {line.billed_unit_price ?? "—"}</span>
                  </p>
                  {varianceType === "overage" && (
                    <p className="mt-1 text-xs text-orange-800">→ {isOpen ? "Will chain" : "Chains"} a <strong>Credit Memo</strong> (SAP item type 003) for the {(Number(line.billed_quantity ?? 0) - Number(line.expected_quantity ?? 0)).toFixed(3)} unit(s) above what was received.</p>
                  )}
                  {varianceType === "price" && (
                    <p className="mt-1 text-xs text-sky-800">→ {isOpen ? "Will chain" : "Chains"} a <strong>Subsequent Credit</strong> (SAP item type 006) for the rate delta on the fully-received quantity.</p>
                  )}
                  {varianceType === "overage+price" && (
                    <p className="mt-1 text-xs text-purple-800">→ {isOpen ? "Will chain" : "Chains"} <strong>both</strong>: a Credit Memo (003) for the extra quantity and a Subsequent Credit (006) for the rate delta on the received portion.</p>
                  )}
                </article>
              );
            })}
          </div>
        </section>

        {record.status !== "open" && (record.chained_credit_memo_ids?.length || record.chained_credit_memo_id || record.chained_credit_memo_error) && (
          <section className="border border-slate-200 bg-white p-4 text-sm" data-testid="three-way-exception-credit-chain-status">
            <h2 className="text-sm font-semibold text-slate-900">Chained credit note(s)</h2>
            {(() => {
              const ids = record.chained_credit_memo_ids?.length ? record.chained_credit_memo_ids : [record.chained_credit_memo_id].filter(Boolean);
              // Legacy records stored a single scalar status string instead of
              // a per-credit array — handle both shapes without indexing into
              // the string (which would render a single character).
              const statusField = record.chained_credit_memo_status;
              const statusFor = (i) => (Array.isArray(statusField) ? statusField[i] : (i === 0 ? statusField : undefined)) || "pending";
              if (!ids.length) return <p className="mt-2 text-xs text-slate-500">No credit note was created yet.</p>;
              return (
                <ul className="mt-2 space-y-1">
                  {ids.map((creditId, i) => (
                    <li key={creditId} className="flex items-center gap-2 font-mono text-xs text-slate-700" data-testid={`three-way-exception-credit-id-${i}`}>
                      {creditId}
                      <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold uppercase text-slate-600">
                        {statusFor(i)}
                      </span>
                    </li>
                  ))}
                </ul>
              );
            })()}
            {record.chained_credit_memo_error && (
              <p className="mt-2 text-xs text-red-700" data-testid="three-way-exception-credit-chain-error">{record.chained_credit_memo_error}</p>
            )}
          </section>
        )}

        {isOpen && (
          <section className="grid gap-4 md:grid-cols-2" data-testid="three-way-exception-actions">
            {/* Option A — Re-enter supplier invoice */}
            <article className="border border-slate-200 bg-white p-5" data-testid="three-way-exception-action-reenter">
              <div className="flex items-start gap-3">
                <FilePenLine className="mt-0.5 text-slate-700" />
                <div>
                  <h2 className="text-base font-semibold text-slate-950">Re-enter supplier invoice</h2>
                  <p className="mt-1 text-sm text-slate-600">Use this if the billed qty/rate was mistyped or the supplier sent a corrected invoice. Nothing is written to SAP. The worklist row reopens for a fresh three-way match.</p>
                </div>
              </div>

              <details className="mt-3 rounded border border-slate-200 bg-slate-50 p-3 text-xs text-slate-700" data-testid="three-way-exception-reenter-explainer">
                <summary className="cursor-pointer font-semibold text-slate-900">What exactly happens when you click this?</summary>
                <ol className="mt-2 list-decimal space-y-1 pl-4">
                  <li>This exception is marked <span className="font-mono">superseded_reentry</span> — no SAP call is made, nothing posts or drafts.</li>
                  <li>The GRN row(s) for this shipment go back to <span className="font-mono">due_for_invoicing</span> on the Due Invoicing worklist.</li>
                  <li>You re-key the supplier invoice from scratch and run the three-way match again — pick this path only when the numbers you typed were wrong, not when the supplier genuinely billed more than the GRN.</li>
                </ol>
              </details>

              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button type="button" variant="outline" className="mt-4 w-full gap-2" disabled={!!busyAction} data-testid="three-way-exception-reenter-button">
                    {busyAction === "reenter" ? <Loader2 size={14} className="animate-spin" /> : <FilePenLine size={14} />}
                    Re-enter supplier invoice
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent data-testid="three-way-exception-reenter-dialog">
                  <AlertDialogHeader>
                    <AlertDialogTitle>Reopen this receipt for re-entry?</AlertDialogTitle>
                    <AlertDialogDescription>
                      This supersedes exception {record.exception_id} and sends the row back to the Due Invoicing worklist as <em>due for invoicing</em>. No SAP writes.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel data-testid="three-way-exception-reenter-cancel">Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={handleReenter} data-testid="three-way-exception-reenter-confirm">Reopen for re-entry</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </article>

            {/* Option B — Accept variance & credit memo */}
            <article className="border border-amber-200 bg-amber-50 p-5" data-testid="three-way-exception-action-accept">
              <div className="flex items-start gap-3">
                <Scale className="mt-0.5 text-amber-800" />
                <div className="flex-1">
                  <h2 className="text-base font-semibold text-amber-950">Accept variance &amp; post automatically</h2>
                  <p className="mt-1 text-sm text-amber-900">Post the SAP supplier invoice at the supplier's <strong>billed</strong> qty/rate, then let the app chain the correcting credit note(s) for you. Zero inventory or MAP impact — proven live on real SAP postings.</p>
                </div>
              </div>

              <details className="mt-3 rounded border border-amber-200 bg-white/70 p-3 text-xs text-amber-900" data-testid="three-way-exception-accept-explainer">
                <summary className="cursor-pointer font-semibold text-amber-950">What exactly happens when you click this?</summary>
                <ol className="mt-2 list-decimal space-y-2 pl-4">
                  <li>
                    <strong>Step 1 — Invoice posts at the billed amount.</strong> You land in the full invoice workspace with
                    the billed qty/rate pre-filled. You submit the invoice as normal; SAP accepts it even though the
                    quantity/price exceeds the GRN, because the receipt link is written as an "Information Provider"
                    reference (relationship role 17) instead of the default matching role — SAP shows the GRN link but
                    no longer force-clamps the line to the received quantity.
                  </li>
                  <li>
                    <strong>Step 2 — Credit note(s) chain automatically</strong>, right after the invoice posts, with no
                    further clicks from you:
                    <ul className="mt-1 list-disc space-y-1 pl-4">
                      <li><strong>Quantity overage</strong> component (billed qty &gt; received qty) → a genuine SAP <strong>Credit Memo</strong> (header type 005, item type 003 — the only item type that reduces invoiced quantity, not just value).</li>
                      <li><strong>Price variance</strong> component (billed rate &gt; PO rate) → a SAP <strong>Subsequent Credit</strong> (header type 005, item type 006 — value-only, for the fully-received quantity).</li>
                      <li>Each credit document carries the full reference bundle: Purchase Order + GRN (Confirmed Inbound Delivery) + the just-posted parent invoice — so SAP clears/nets it straight against the parent.</li>
                    </ul>
                  </li>
                  <li>
                    <strong>What moves in the ledger.</strong> Verified against real SAP journal entries: the invoice debits
                    GRNI + Input Tax and credits Creditors (Accounts Payable) for the full billed amount; the credit note(s)
                    reverse the GRNI/tax delta and credit Creditors back down to the PO/GRN-priced amount. <strong>Neither
                    document touches Inventory, Material or Stock accounts</strong> — inventory valuation (MAP) is
                    unaffected.
                  </li>
                  <li>
                    <strong>Guardrails before this runs.</strong> Invoicing is blocked if an older unbilled shipment for the
                    same supplier is still open (FIFO), if a sibling receipt for the same PO line could absorb the
                    variance instead, or if an earlier overage on the same PO line is still waiting on its own credit
                    note. Materials Hub also won't let a new GRN happen for this product while an unresolved overage is
                    open.
                  </li>
                  <li>
                    <strong>If a credit note fails to post</strong>, this exception's status becomes <em>"Invoice posted ·
                    credit note failed"</em> and the SAP error is shown here — the invoice itself stays posted; only the
                    correcting credit needs manual retry/escalation.
                  </li>
                </ol>
              </details>

              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button type="button" className="mt-4 w-full gap-2 bg-amber-700 text-white hover:bg-amber-800" disabled={!!busyAction} data-testid="three-way-exception-accept-button">
                    {busyAction === "accept-variance" ? <Loader2 size={14} className="animate-spin" /> : <Scale size={14} />}
                    Accept variance &amp; proceed
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent data-testid="three-way-exception-accept-dialog">
                  <AlertDialogHeader>
                    <AlertDialogTitle>Accept the billed variance?</AlertDialogTitle>
                    <AlertDialogDescription>
                      This mints an approved three-way match at the <strong>billed</strong> qty/rate and takes you straight
                      to the full invoice workspace — the manual three-way match step is skipped. Once you post that
                      invoice, the correcting credit note(s) (Credit Memo for overage, Subsequent Credit for price) chain
                      automatically — see "What exactly happens" above for the full detail.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel data-testid="three-way-exception-accept-cancel">Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={handleAcceptVariance} data-testid="three-way-exception-accept-confirm" className="bg-amber-700 hover:bg-amber-800">
                      Accept variance
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </article>
          </section>
        )}

        {!isOpen && (
          <section className={`border p-4 text-sm ${resolutionTone[record.status] || "border-emerald-200 bg-emerald-50 text-emerald-900"}`} data-testid="three-way-exception-resolved-banner">
            This exception is <strong>{resolutionLabel[record.status] || record.status}</strong>. {resolvedBannerMessage[record.status] || "No further action is required here."}
            {record.accepted_match_id && (
              <p className="mt-1 font-mono text-xs">Linked three-way match: {record.accepted_match_id}</p>
            )}
          </section>
        )}
      </div>
    </main>
  );
}
